import { Field, useFormikContext } from "formik";
import { FC, useRef } from "react";
import React from "react";
import { useLocale } from "@/contexts/localeContext";
import { TranslationKeys } from "@/contexts/TranslationKeys";
import { ExpiryReason } from "@/models/ExpiryReason";
import { EXPIRY_UNIT_TYPE, MAX_DAYS, MAX_DAYS_WARNING, MAX_MONTHS, MAX_WEEKS, MIN_DAYS_WARNING } from "@/constants/expiry-reason-constant";
import { Name } from "@/models/Name";
import { WizardStepLayout } from '../wizard/wizard-summary-layout';
import SettingSelectField from "../settingFields/SettingSelectField";
import Label from "../label/label";
import Tooltip from "../Tooltip/Tooltip";
import { validateIntegerWithinBounds } from "@/utilities/Validation";

interface DetailsStepProps {
    onNext: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    setStepDescriptions: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
}

const DetailStep: FC<DetailsStepProps> = ({ onNext, setStepDescriptions }) => {
    const { translate } = useLocale();
    const { errors, values, isValid } = useFormikContext<ExpiryReason>();
    const inputRef = useRef<HTMLInputElement>(null);
    const unitOptions: Name[] = [
        { value: EXPIRY_UNIT_TYPE.DAYS.toString(), label: translate.byKey(TranslationKeys.days) },
        { value: EXPIRY_UNIT_TYPE.WEEKS.toString(), label: translate.byKey(TranslationKeys.weeks) },
        { value: EXPIRY_UNIT_TYPE.MONTHS.toString(), label: translate.byKey(TranslationKeys.months) },
    ];
    
    const validateDurationValue = (value: any, durationUnit: number, translate: any) => {
        let max = MAX_DAYS;

        if (durationUnit === EXPIRY_UNIT_TYPE.WEEKS) {
            max = MAX_WEEKS;
        }

        if (durationUnit === EXPIRY_UNIT_TYPE.MONTHS) {
            max = MAX_MONTHS;
        }

        return validateIntegerWithinBounds(Number(value), 1, max, translate, true);
    };

    const handleNext = (e: React.MouseEvent<HTMLButtonElement>) => {
        setStepDescriptions(prev => ({ ...prev, step1: values.name }));
        onNext(e);
    };

    return <WizardStepLayout onNext={handleNext} nextDisabled={!isValid}>
        <div className="flex flex-col overflow-y-auto" style={{ height: '85%' }}>
            <div className="text-portal-blue-primary-shade2 text-xl font-semibold">
                {translate.byKey(TranslationKeys.details)}
            </div>

            <div className='flex flex-col pt-2'>
                <div className="flex flex-row justify-between" id='nameLabel'>
                    <Label
                        htmlFor="name"
                        className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.name)}:</Label>
                    {errors.name ? <span className='text-red-600 text-xs'>{errors.name}</span> : null}
                </div>
                <div className="flex flex-row justify-between">
                    <Field
                        type="text"
                        name="name"
                        data-cy="nameField"
                        id="nameInput"
                        innerRef={inputRef}
                        className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm w-full bg-gray-input" />
                </div>
            </div>
            
            <div className="flex flex-row pt-2">
                <SettingSelectField
                    accessor="durationUnit"
                    displayName={translate.byKey(TranslationKeys.units)}
                    required
                    options={unitOptions} 
                    className="w-full"/>
            </div>

            <div className='flex flex-col'>
                <div className="flex flex-row justify-between" id='durationValueLabel'>
                    <Label
                        htmlFor="durationValue"
                        className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.duration)}:</Label>
                    {errors.durationValue ? <span className='text-red-600 text-xs'>{errors.durationValue}</span> : null}
                </div>
                <div className="flex flex-row justify-between">
                    <Field
                        type="text"
                        name="durationValue"
                        data-cy="durationValueField"
                        id="durationValueId"
                        innerRef={inputRef}
                        className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm w-full bg-gray-input" 
                        validate={(value: any) => validateDurationValue(value, Number(values.durationUnit), translate)} />
                </div>
            </div>

            <div className='flex flex-col pt-2'>
                <div className="flex flex-row justify-between" id='durationWarnValueLabel'>
                    <Tooltip text={translate.byKey(TranslationKeys.durationWarningTip)} direction="bottom" show={true} contentPositionX='start'>
                        <Label
                            htmlFor="durationWarnValue"
                            className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.durationWarning)}:<span className="italic text-gray-500">&nbsp;({TranslationKeys.days})</span></Label>
                    </Tooltip>
                    {errors.durationWarnValue ? <span className='text-red-600 text-xs'>{errors.durationWarnValue}</span> : null}
                </div>
                <div className="flex flex-row justify-between">
                    <Field
                        type="text"
                        name="durationWarnValue"
                        data-cy="durationWarnValueField"
                        id="durationWarnValueId"
                        innerRef={inputRef}
                        className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm w-full bg-gray-input" 
                        validate={(value: any) => validateIntegerWithinBounds(Number(value), MIN_DAYS_WARNING, MAX_DAYS_WARNING, translate, false)}/>
                </div>
            </div>
        </div>
    </WizardStepLayout>
};

export default DetailStep;